import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Clock, Moon, Sparkles, Heart, Send, Loader2, Check, ChevronDown, Users, Beer, Wine } from 'lucide-react';
import { FEST_INFO, CAMP_MEMBERS } from '../data/camp-data';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const kimmo = CAMP_MEMBERS.find(m => m.id === 'kimmo');
const lisa = CAMP_MEMBERS.find(m => m.id === 'lisa');

export default function OktoberFest() {
  const [rsvps, setRsvps] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', co_create: '', dream: '', sleep_over: false, attending: true });

  const fetchRsvps = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rsvp`);
      setRsvps(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchRsvps(); }, [fetchRsvps]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/rsvp`, form);
      setSubmitted(true);
      fetchRsvps();
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen relative" data-testid="oktoberfest-page">
      {/* Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[var(--mc-bg)]" />
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `radial-gradient(circle at 20% 30%, var(--mc-pink) 0%, transparent 50%),
                            radial-gradient(circle at 80% 70%, #FFD700 0%, transparent 50%),
                            radial-gradient(circle at 50% 50%, var(--mc-purple) 0%, transparent 60%)`,
        }} />
      </div>

      <div className="relative z-10">
        {/* Hero invitation */}
        <section className="pt-16 sm:pt-24 pb-12 px-4 sm:px-8">
          <div className="max-w-3xl mx-auto text-center">
            {/* Hosts */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="flex items-center justify-center gap-6 mb-8">
              <div className="text-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden mx-auto mb-2 glow-border-pink">
                  <img src={kimmo.avatar} alt="Kimmo" className="w-full h-full object-cover object-top" />
                </div>
                <span className="text-xs font-mono text-[var(--mc-pink)]">Kimmo</span>
              </div>
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6, type: 'spring' }}>
                <Heart className="w-8 h-8 text-[var(--mc-pink)]" style={{ filter: 'drop-shadow(0 0 10px var(--mc-pink-glow))' }} />
              </motion.div>
              <div className="text-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden mx-auto mb-2 glow-border-cyan">
                  <img src={lisa.avatar} alt="Lisa" className="w-full h-full object-cover object-top" />
                </div>
                <span className="text-xs font-mono text-[var(--mc-cyan)]">Lisa</span>
              </div>
            </motion.div>

            {/* Invitation text */}
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
              className="text-xs tracking-[0.3em] uppercase text-[var(--mc-cyan)] font-mono mb-4">
              Du är inbjuden
            </motion.p>

            <motion.h1 initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1, duration: 0.8 }}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase neon-text leading-tight">
              MUY COMPLICADO
            </motion.h1>

            <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8 }}
              className="font-heading text-5xl sm:text-6xl lg:text-8xl font-black uppercase mt-2 leading-none"
              style={{ color: '#FFD700', textShadow: '0 0 20px rgba(255,215,0,0.5), 0 0 40px rgba(255,215,0,0.3)' }}>
              Oktober Fest
            </motion.h2>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
              className="mt-8 text-base sm:text-lg text-[var(--mc-text-sec)] max-w-lg mx-auto leading-relaxed">
              Kimmo & Lisa bjuder in dig till årets galantaste, galnaste och mest
              kärleksfulla fest. Muy Complicado art car på plats. Tilltugg, dricka
              och framförallt — en show du sent kommer glömma.
            </motion.p>

            {/* Scroll indicator */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.4 }} transition={{ delay: 2.5 }}
              className="mt-12">
              <ChevronDown className="w-6 h-6 mx-auto text-[var(--mc-text-muted)] animate-bounce" />
            </motion.div>
          </div>
        </section>

        {/* Details */}
        <section className="py-16 px-4 sm:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                className="glass rounded-2xl p-6 text-center">
                <Clock className="w-8 h-8 mx-auto mb-3 text-[var(--mc-pink)]" style={{ filter: 'drop-shadow(0 0 8px var(--mc-pink-glow))' }} />
                <p className="font-heading text-2xl font-bold text-white">26 SEPT</p>
                <p className="text-sm text-[var(--mc-text-sec)] mt-1">Kl 18:00 → sent</p>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                className="glass rounded-2xl p-6 text-center">
                <MapPin className="w-8 h-8 mx-auto mb-3 text-[var(--mc-cyan)]" style={{ filter: 'drop-shadow(0 0 8px var(--mc-cyan-glow))' }} />
                <p className="font-heading text-lg font-bold text-white">Torpvägen 20</p>
                <p className="text-sm text-[var(--mc-text-sec)] mt-1">147 63 Uttran</p>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
                className="glass rounded-2xl p-6 text-center">
                <Moon className="w-8 h-8 mx-auto mb-3 text-[var(--mc-purple)]" style={{ filter: 'drop-shadow(0 0 8px rgba(157,76,221,0.5))' }} />
                <p className="font-heading text-lg font-bold text-white">Sova över?</p>
                <p className="text-sm text-[var(--mc-text-sec)] mt-1">Platser finns — ange i RSVP</p>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
                className="glass rounded-2xl p-6 text-center">
                <Sparkles className="w-8 h-8 mx-auto mb-3 text-[#FFD700]" style={{ filter: 'drop-shadow(0 0 8px rgba(255,215,0,0.5))' }} />
                <p className="font-heading text-lg font-bold text-white">Vi bjuder</p>
                <p className="text-sm text-[var(--mc-text-sec)] mt-1">Tilltugg, dricka & en show</p>
              </motion.div>
            </div>

            {/* Drink info */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="glass rounded-2xl p-6 mt-4 text-center glow-border-pink">
              <div className="flex items-center justify-center gap-3 mb-3">
                <Wine className="w-6 h-6 text-[var(--mc-pink)]" />
                <Beer className="w-6 h-6 text-[#FFD700]" />
              </div>
              <p className="font-heading text-base font-bold text-white mb-2">Ta med din favoritdryck!</p>
              <p className="text-sm text-[var(--mc-text-sec)] leading-relaxed">
                Ta med det du vill dricka till dig själv — och givetvis om du vill bjuda andra.
                <br />Vi fixar tilltugg och grundutbudet. Du fixar din favorit.
              </p>
            </motion.div>

            {/* Vibe text */}
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              className="text-center mt-12 font-comic text-xl sm:text-2xl text-[var(--mc-text-sec)] max-w-md mx-auto leading-relaxed">
              Bra vibe. Galna människor. Art car.
              <br />Det blir legendariskt.
            </motion.p>

            {/* Tema box */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
              className="mt-14 relative overflow-hidden rounded-3xl"
              style={{
                background: 'linear-gradient(135deg, rgba(255,215,0,0.08) 0%, rgba(255,0,127,0.06) 50%, rgba(157,76,221,0.08) 100%)',
                border: '1px solid rgba(255,215,0,0.2)',
                boxShadow: '0 0 40px rgba(255,215,0,0.1), inset 0 0 40px rgba(255,215,0,0.03)',
              }}>
              {/* Gold accent lines */}
              <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #FFD700, transparent)' }} />
              <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, var(--mc-pink), transparent)' }} />

              <div className="px-8 py-10 sm:px-12 sm:py-14 text-center">
                <Beer className="w-10 h-10 mx-auto mb-4 text-[#FFD700]" style={{ filter: 'drop-shadow(0 0 12px rgba(255,215,0,0.5))' }} />
                <p className="text-xs tracking-[0.3em] uppercase text-[var(--mc-text-muted)] font-mono mb-3">Kvällens tema</p>
                <h3 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase leading-tight"
                  style={{ color: '#FFD700', textShadow: '0 0 15px rgba(255,215,0,0.4), 0 0 30px rgba(255,215,0,0.2)' }}>
                  Oktoberfest
                </h3>
                <div className="mt-6 h-px w-20 mx-auto" style={{ background: 'linear-gradient(90deg, transparent, #FFD700, transparent)' }} />
                <p className="mt-6 text-sm text-[var(--mc-text-sec)] max-w-sm mx-auto leading-relaxed">
                  Vi börjar kvällen utomhus — klä er efter det!
                  <br />Tips: en skön rock.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* RSVP */}
        <section className="py-16 px-4 sm:px-8" id="rsvp">
          <div className="max-w-lg mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
              <p className="text-xs tracking-[0.3em] uppercase text-[var(--mc-cyan)] font-mono mb-4">Är du med?</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">RSVP</h2>
            </motion.div>

            {submitted ? (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="glass rounded-2xl p-8 text-center glow-border-pink">
                <Check className="w-12 h-12 mx-auto mb-4 text-[#39FF14]" />
                <h3 className="font-heading text-xl font-bold text-white mb-2">Du är med!</h3>
                <p className="text-sm text-[var(--mc-text-sec)]">Vi ses den 26 september. Det blir magiskt.</p>
              </motion.div>
            ) : (
              <motion.form onSubmit={handleSubmit} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="glass rounded-2xl p-6 sm:p-8 space-y-5" data-testid="rsvp-form">

                <div>
                  <label className="text-xs tracking-[0.15em] uppercase text-[var(--mc-text-muted)] font-mono mb-2 block">Ditt namn *</label>
                  <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                    placeholder="Vem är du?" required data-testid="rsvp-name"
                    className="w-full bg-transparent border-b-2 border-[var(--mc-cyan)]/30 focus:border-[var(--mc-cyan)] text-white text-sm font-mono py-3 outline-none transition-colors" />
                </div>

                <div>
                  <label className="text-xs tracking-[0.15em] uppercase text-[var(--mc-text-muted)] font-mono mb-2 block">E-post</label>
                  <input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
                    placeholder="Din e-post (för eventuel info)" type="email" data-testid="rsvp-email"
                    className="w-full bg-transparent border-b-2 border-[var(--mc-cyan)]/30 focus:border-[var(--mc-cyan)] text-white text-sm font-mono py-3 outline-none transition-colors" />
                </div>

                <div>
                  <label className="text-xs tracking-[0.15em] uppercase text-[var(--mc-text-muted)] font-mono mb-2 block">
                    Vill du co-create något?
                  </label>
                  <textarea value={form.co_create} onChange={e => setForm(f => ({...f, co_create: e.target.value}))}
                    placeholder="En show? Ett DJ-set? En installation? Eller bara din närvaro..." rows={2} data-testid="rsvp-cocreate"
                    className="w-full bg-transparent border-b-2 border-[var(--mc-cyan)]/30 focus:border-[var(--mc-cyan)] text-white text-sm font-mono py-3 outline-none resize-none transition-colors" />
                </div>

                <div>
                  <label className="text-xs tracking-[0.15em] uppercase text-[var(--mc-text-muted)] font-mono mb-2 block">
                    Har du en dröm?
                  </label>
                  <textarea value={form.dream} onChange={e => setForm(f => ({...f, dream: e.target.value}))}
                    placeholder="Något du drömmer om. Stort eller smått." rows={2} data-testid="rsvp-dream"
                    className="w-full bg-transparent border-b-2 border-[var(--mc-cyan)]/30 focus:border-[var(--mc-cyan)] text-white text-sm font-mono py-3 outline-none resize-none transition-colors" />
                </div>

                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => setForm(f => ({...f, sleep_over: !f.sleep_over}))}
                    data-testid="rsvp-sleep"
                    className={`w-6 h-6 rounded-md border-2 flex items-center justify-center cursor-pointer transition-colors
                      ${form.sleep_over ? 'bg-[var(--mc-purple)]/30 border-[var(--mc-purple)]' : 'border-white/20'}`}>
                    {form.sleep_over && <Check className="w-4 h-4 text-[var(--mc-purple)]" />}
                  </button>
                  <span className="text-sm text-[var(--mc-text-sec)]">Jag vill sova över</span>
                </div>

                <button type="submit" disabled={submitting} data-testid="rsvp-submit"
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-full
                           bg-[var(--mc-pink)] text-white font-heading text-sm tracking-[0.15em] uppercase
                           hover:scale-[1.02] transition-transform cursor-pointer disabled:opacity-40">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  Jag är med!
                </button>
              </motion.form>
            )}
          </div>
        </section>

        {/* Footer */}
        <footer className="py-16 px-4 text-center">
          <div className="max-w-md mx-auto">
            <div className="h-px w-32 mx-auto mb-8" style={{ background: 'linear-gradient(90deg, transparent, var(--mc-pink), transparent)' }} />
            <p className="font-heading text-sm sm:text-base tracking-[0.15em] uppercase text-[var(--mc-text-sec)] neon-text"
              style={{ textShadow: '0 0 10px var(--mc-pink-glow), 0 0 20px var(--mc-pink-glow)' }}>
              Vi möts i det som inte går att förklara
            </p>
            <p className="text-xs font-mono text-[var(--mc-text-muted)] mt-4 tracking-[0.2em] uppercase">
              Muy Complicado — Oktober Fest 2026
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
